import * as mediasoupClient from 'mediasoup-client';
import { store } from '../../store';
import MediasoupCanProduceError from './MediasoupError';
import { mediaTypes } from '../../constants/mediaTypes';
import Media, { createSilentAudioTrack, createCameraOffVideoTrack } from '../media/Media';
import { getLayersByLevel } from '../../constants/scalabilityLayers';

function createSoftDisabledTrack(mediaType) {
  if (mediaType === mediaTypes.audioType) {
    return createSilentAudioTrack();
  }

  if (mediaType === mediaTypes.videoType) {
    return createCameraOffVideoTrack();
  }

  return null;
}

class Mediasoup {
  constructor(socketRequest, videoConferenceActions) {
    this.mediaStore = null;
    this.media = new Media();
    this.socketRequest = socketRequest;
    this.videoConferenceActions = videoConferenceActions;
    this.device = null;
    this.producerTransport = null;
    this.consumerTransport = null;
    this.isResumeVideoAfterCloseScreenShare = false;
    this.isInitialized = false;
    this.softDisabledLocalMedia = {
      [mediaTypes.audioType]: null,
      [mediaTypes.videoType]: null,
    };
    this.parkedAudioProducer = null;
    this._consumerTransportOperation = Promise.resolve();
    this.transportActions = {
      connectTransport: 'connectTransport',
    };
    this.transportEvents = {
      connect: 'connect',
      produce: 'produce',
      connectionstatechange: 'connectionstatechange',
    };
  }

  async init(roomId, rtpCapabilities, transports) {
    this._defineStore();
    await this._loadDevice(rtpCapabilities);
    this._initTransports(roomId, transports);
    this.isInitialized = true;
  }

  stopMediaTransfer() {
    this._cleanupSoftDisabledLocalMedia(mediaTypes.audioType);
    this._cleanupSoftDisabledLocalMedia(mediaTypes.videoType);

    if (this.producerTransport && !this.producerTransport.closed) {
      this.producerTransport.close();
    }

    if (this.consumerTransport && !this.consumerTransport.closed) {
      this.consumerTransport.close();
    }

    this.closeAllProducers();
    this.removeAllConsumers();
  }

  _getLocalMediaItem(mediaType) {
    return this.mediaStore.localMedia?.[mediaType] ?? null;
  }

  _getLocalProducer(mediaType) {
    return this._getLocalMediaItem(mediaType)?.producer ?? null;
  }

  _cleanupSoftDisabledLocalMedia(mediaType) {
    const softDisabledMedia = this.softDisabledLocalMedia?.[mediaType];

    if (!softDisabledMedia) {
      return;
    }

    if (softDisabledMedia.cleanup) {
      try {
        softDisabledMedia.cleanup();
      } catch (error) {
        // placeholder track may already be stopped
      }
    }

    this.softDisabledLocalMedia[mediaType] = null;
  }

  async softDisableLocalProducer(mediaType) {
    const producer = this._getLocalProducer(mediaType);

    if (!producer || producer.closed) {
      return false;
    }

    if (mediaType === mediaTypes.audioType) {
      const audioTrack = producer.track;

      if (audioTrack) {
        audioTrack.enabled = false;
      }

      this.mediaStore.setLocalMediaTrackState(mediaType, audioTrack, false);

      return true;
    }

    const softDisabledMedia = createSoftDisabledTrack(mediaType);

    if (!softDisabledMedia?.track) {
      return false;
    }

    const previousTrack = producer.track;

    this._cleanupSoftDisabledLocalMedia(mediaType);

    await producer.replaceTrack({
      track: softDisabledMedia.track,
    });

    if (previousTrack && previousTrack.readyState !== 'ended') {
      previousTrack.stop();
    }

    this.softDisabledLocalMedia[mediaType] = softDisabledMedia;
    this.media.removeLocalMedia(mediaType);
    this.mediaStore.setLocalMediaTrackState(mediaType, softDisabledMedia.track, false);

    return true;
  }

  async softEnableLocalProducer(mediaType, isKeepingPreviousState = false) {
    const producer = this._getLocalProducer(mediaType);

    if (!producer || producer.closed) {
      await this.produce(mediaType, isKeepingPreviousState);
      return true;
    }

    if (mediaType === mediaTypes.audioType) {
      const audioTrack = producer.track;

      if (audioTrack) {
        audioTrack.enabled = true;
      }

      this.mediaStore.setLocalMediaTrackState(mediaType, audioTrack, true);

      return true;
    }

    const stream = await this.media.getLocalMedia(mediaType, null, isKeepingPreviousState);
    const [track] = stream.getTracks();

    if (!track) {
      return false;
    }

    await producer.replaceTrack({
      track,
    });

    this._cleanupSoftDisabledLocalMedia(mediaType);
    this.mediaStore.setLocalMediaTrackState(mediaType, track, true);

    return true;
  }

  async produce(mediaType, isKeepingPreviousState = false) {
    const stream = await this.canBeProduced(mediaType, isKeepingPreviousState);
    const { isScreenShare, isVideo, isAudio } = this.mediaStore.localMedia;
    if (stream) {
      await this.produceStream(stream, mediaType);
      if (mediaType === mediaTypes.screenType && isVideo) {
        this.isResumeVideoAfterCloseScreenShare = true;
      }
      if (mediaType === mediaTypes.videoType) {
        this.isResumeVideoAfterCloseScreenShare = false;
      }
    }
    return { isScreenShare, isVideo, isAudio };
  }

  async parkAudioProducer(producerId) {
    const audioMedia = this.mediaStore.localMedia?.[mediaTypes.audioType];
    const producer = audioMedia?.producer;

    if (!producer || audioMedia.producerId !== producerId) {
      return false;
    }

    if (
      this.parkedAudioProducer?.producer &&
      !this.parkedAudioProducer.producer.closed &&
      this.parkedAudioProducer.producerId !== producerId
    ) {
      this.closeParkedAudioProducer();
    }

    if (
      this.parkedAudioProducer?.producer &&
      !this.parkedAudioProducer.producer.closed &&
      this.parkedAudioProducer.producerId === producerId
    ) {
      this.mediaStore.removeLocalMediaTrack(producerId, {
        shouldCloseProducer: false,
      });

      this.media.removeLocalMedia(mediaTypes.audioType);

      return true;
    }

    const silentAudio = createSilentAudioTrack();

    if (!silentAudio?.track) {
      return false;
    }

    const previousTrack = producer.track;

    await producer.replaceTrack({ track: silentAudio.track });

    if (previousTrack && previousTrack.readyState !== 'ended') {
      previousTrack.stop();
    }

    this.parkedAudioProducer = {
      producer,
      producerId,
      silentTrack: silentAudio.track,
      audioContext: silentAudio.audioContext,
      oscillator: silentAudio.oscillator,
    };

    this.mediaStore.removeLocalMediaTrack(producerId, {
      shouldCloseProducer: false,
    });

    this.media.removeLocalMedia(mediaTypes.audioType);

    return true;
  }

  closeParkedAudioProducer() {
    const { parkedAudioProducer } = this;

    if (!parkedAudioProducer) {
      return;
    }

    const { producer, silentTrack, audioContext, oscillator } = parkedAudioProducer;

    if (oscillator?.stop) {
      try {
        oscillator.stop();
      } catch (error) {
        // oscillator may already be stopped
      }
    }

    if (silentTrack && silentTrack.readyState !== 'ended') {
      silentTrack.stop();
    }

    if (producer && !producer.closed) {
      producer.close();
    }

    if (audioContext?.close) {
      audioContext.close();
    }

    this.parkedAudioProducer = null;
  }

  async produceStream(stream, mediaType) {
    const isMediaTrackAlreadyExist = this.mediaStore.localMedia?.[mediaType];

    if (stream && !isMediaTrackAlreadyExist) {
      const params = {};
      [params.track] = stream.getTracks();
      params.appData = { mediaType };

      if (mediaType === mediaTypes.videoType || mediaType === mediaTypes.screenType) {
        params.encodings =
          mediaType === mediaTypes.videoType
            ? this.media.videoEncodings
            : this.media.screenshareEncodings;
        params.codecOptions = this.media.videoCodecOptions;
      }

      const producer = await this.producerTransport.produce(params);

      this.mediaStore.setLocalMediaTrack(producer, mediaType);
      this._onProducerEvents(producer);
    }
  }

  async canBeProduced(mediaType, isKeepingPreviousState = false) {
    const stream = await this.media.getLocalMedia(mediaType, null, isKeepingPreviousState);
    const canBeProduced = this.device.canProduce(
      mediaType === mediaTypes.screenType ? mediaTypes.videoType : mediaTypes.audioType,
    );
    if (!canBeProduced) {
      throw new MediasoupCanProduceError(mediaType);
    }
    return stream;
  }

  runConsumerTransportOperation(operationName, callback) {
    this._consumerTransportOperation = this._consumerTransportOperation
      .catch(error => {
        console.error('Previous consumer transport operation failed', {
          operationName,
          error,
        });
      })
      .then(async () => {
        try {
          return await callback();
        } catch (error) {
          console.error('Consumer transport operation failed', {
            operationName,
            error,
          });
          throw error;
        }
      });

    return this._consumerTransportOperation;
  }

  async _stopExistingConsumerByMediaType(roomId, peerId, mediaType, isTeacher) {
    let remoteMedia;
    if (isTeacher) {
      remoteMedia = this.mediaStore.teacherMedia;
    } else {
      remoteMedia = this.mediaStore.remoteMedia.get(peerId);
    }

    if (!remoteMedia) {
      return;
    }

    const existingProducerEntryByType = Object.entries(remoteMedia?.producers ?? {}).find(
      ([, producerData]) => producerData?.type === mediaType,
    );

    const existingConsumer = remoteMedia?.media?.[mediaType]?.consumer;
    const existingConsumerId = existingConsumer?.id;

    if (!existingProducerEntryByType || !existingConsumerId) {
      return;
    }

    const [oldProducerId] = existingProducerEntryByType;

    console.warn('Mediasoup consume replacing existing producer of same mediaType', {
      peerId,
      mediaType,
      oldProducerId,
      newConsumerWillStart: true,
      isTeacher,
    });

    try {
      await this.socketRequest(this.videoConferenceActions.stopConsumeStream, {
        roomId,
        consumerId: existingConsumerId,
      });
    } catch (error) {
      console.error('Mediasoup stopConsumeStream for existing consumer failed', {
        peerId,
        mediaType,
        oldProducerId,
        existingConsumerId,
        isTeacher,
        error,
      });
    }

    try {
      if (existingConsumer && !existingConsumer.closed) {
        existingConsumer.close();
      }
    } catch (error) {
      console.error('Mediasoup existing consumer close failed', {
        peerId,
        mediaType,
        oldProducerId,
        existingConsumerId,
        isTeacher,
        error,
      });
    }

    if (isTeacher) {
      this.mediaStore.removeTeacherMediaTrack(oldProducerId, true);
    } else {
      this.mediaStore.removeRemoteMediaTrack(peerId, oldProducerId, true);
    }

    await new Promise(resolve => {
      setTimeout(resolve, 50);
    });
  }

  async _cleanupFailedConsume(roomId, consumerId, consumer, context = {}) {
    try {
      if (consumer && !consumer.closed) {
        consumer.close();
      }
    } catch (error) {
      console.error('Mediasoup cleanup failed: local consumer close failed', {
        roomId,
        consumerId,
        context,
        error,
      });
    }

    try {
      if (consumerId) {
        await this.socketRequest(this.videoConferenceActions.stopConsumeStream, {
          roomId,
          consumerId,
        });
      }
    } catch (error) {
      console.error('Mediasoup cleanup failed: stopConsumeStream failed', {
        roomId,
        consumerId,
        context,
        error,
      });
    }
  }

  async closeProducer(producerId, mediaType, options = {}) {
    const { shouldParkAudioProducer = false } = options;

    if (mediaType && !producerId) {
      const { producerId: localMediaProducerId } = this.mediaStore.localMedia[mediaType];

      if (shouldParkAudioProducer && mediaType === mediaTypes.audioType) {
        return this.parkAudioProducer(localMediaProducerId);
      }

      this.mediaStore.removeLocalMediaTrack(localMediaProducerId);
      this.media.removeLocalMedia(mediaType);

      return false;
    }

    if (!mediaType && producerId) {
      const mediaTypeByProducerId = Object.values(mediaTypes).find(
        mediaTypeItem => this.mediaStore.localMedia[mediaTypeItem]?.producerId === producerId,
      );

      if (shouldParkAudioProducer && mediaTypeByProducerId === mediaTypes.audioType) {
        return this.parkAudioProducer(producerId);
      }

      if (mediaTypeByProducerId) {
        this.media.removeLocalMedia(mediaTypeByProducerId);
      }

      this.mediaStore.removeLocalMediaTrack(producerId);
    }

    return false;
  }

  closeAllProducers() {
    const videoProducerId = this.mediaStore?.localMedia?.[mediaTypes.videoType]?.producerId;
    const audioProducerId = this.mediaStore?.localMedia?.[mediaTypes.audioType]?.producerId;
    const screenShareProducerId = this.mediaStore?.localMedia?.[mediaTypes.screenType]?.producerId;

    if (videoProducerId) {
      this.closeProducer(videoProducerId);
    }
    if (audioProducerId) {
      this.closeProducer(audioProducerId);
    }
    if (screenShareProducerId) {
      this.closeProducer(screenShareProducerId);
    }
  }

  async consume(
    roomId,
    producerInfo,
    peerId,
    isTeacher,
    scalabilityLevel,
    ignoreStreamsBlocking = false,
  ) {
    return this.runConsumerTransportOperation('consume', async () => {
      console.log('Mediasoup consume', { producerInfo, peerId, isTeacher, scalabilityLevel });
      const { producerId, mediaType, peerName, userId } = producerInfo;
      const displayName = peerName || 'Неизвестное имя';
      const { rtpCapabilities } = this.device;

      let remoteMedia;

      if (isTeacher) {
        if (!this.mediaStore.teacherMedia) {
          this.mediaStore.setTeacherMedia(peerId, displayName, userId);
        }

        remoteMedia = this.mediaStore.teacherMedia;
      } else {
        if (!this.mediaStore.remoteMedia.has(peerId)) {
          this.mediaStore.setRemoteMedia(peerId, displayName, userId);
        }

        remoteMedia = this.mediaStore.remoteMedia.get(peerId);
      }

      if (!remoteMedia) {
        console.warn('Mediasoup consume skipped: failed to create remoteMedia', {
          peerId,
          producerId,
          mediaType,
          isTeacher,
        });

        return;
      }

      const existingProducer = remoteMedia?.producers?.[producerId];
      if (existingProducer) {
        console.warn('Mediasoup consume skipped: producer already exists', {
          peerId,
          producerId,
          mediaType,
          isTeacher,
        });
        return;
      }

      const existingProducerEntryByType = Object.entries(remoteMedia?.producers ?? {}).find(
        ([, producerData]) => producerData?.type === mediaType,
      );

      if (existingProducerEntryByType) {
        const [oldProducerId] = existingProducerEntryByType;

        if (oldProducerId !== producerId) {
          await this._stopExistingConsumerByMediaType(roomId, peerId, mediaType, isTeacher);

          if (isTeacher) {
            remoteMedia = this.mediaStore.teacherMedia;
          } else {
            remoteMedia = this.mediaStore.remoteMedia.get(peerId);
          }
        } else {
          console.warn('Mediasoup consume skipped: same producer already attached by mediaType', {
            peerId,
            producerId,
            mediaType,
            isTeacher,
          });
          return;
        }
      }

      const isStreamBlockingEnabled = remoteMedia.blockedStreams.streamsBlocking[mediaType];

      if (isStreamBlockingEnabled && !ignoreStreamsBlocking) {
        if (isTeacher) {
          this.mediaStore.setTeacherBlockedStreams(producerId, mediaType);
        } else {
          this.mediaStore.setRemoteMediaBlockedStreams(peerId, producerId, mediaType);
        }

        return;
      }

      if (isStreamBlockingEnabled && ignoreStreamsBlocking) {
        if (isTeacher) {
          this.mediaStore.setTeacherStreamsBlockingFlag(mediaType, false);
        } else {
          this.mediaStore.setRemoteMediaStreamsBlockingFlag(peerId, mediaType, false);
        }
      }
      const response = await this.socketRequest(this.videoConferenceActions.startConsumeStream, {
        rtpCapabilities,
        consumerTransportId: this.consumerTransport.id,
        producerId,
        roomId,
      });

      const { consumerId, kind, rtpParameters } = response.data.payload.consumer;
      const codecOptions = {};
      let consumer = null;

      try {
        consumer = await this.consumerTransport.consume({
          id: consumerId,
          producerId,
          kind,
          rtpParameters,
          codecOptions,
        });

        const { spatialLayers, temporalLayers } = mediasoupClient.parseScalabilityMode(
          consumer.rtpParameters.encodings[0].scalabilityMode,
        );

        if (isTeacher) {
          this.mediaStore.setTeacherMediaTrack(peerId, producerId, mediaType, consumer, {
            spatialLayers,
            temporalLayers,
          });
        } else {
          this.mediaStore.setRemoteMediaTrack(peerId, producerId, mediaType, consumer, {
            spatialLayers,
            temporalLayers,
          });
        }

        if (scalabilityLevel) {
          const layers = getLayersByLevel(scalabilityLevel, { spatialLayers, temporalLayers });
          const { spatialLayer, temporalLayer } = layers;

          this.setPreferredScalabilityLayer(peerId, consumerId, layers, isTeacher);

          await this.socketRequest(this.videoConferenceActions.setLayers, {
            roomId,
            consumerId,
            spatialLayer: String(spatialLayer),
            temporalLayer: String(temporalLayer),
          });
        }

        this._onConsumerEvents(consumer);
      } catch (error) {
        console.error('Mediasoup consume transport step failed', {
          peerId,
          producerId,
          mediaType,
          isTeacher,
          roomId,
          consumerId,
          error,
        });

        await this._cleanupFailedConsume(roomId, consumerId, consumer, {
          peerId,
          producerId,
          mediaType,
          isTeacher,
          stage: 'consumerTransport.consume',
        });

        throw error;
      }
    });
  }

  consumeEmpty(peerId, name, isTeacher, isIndividualLesson, userId = null) {
    const displayName = name || 'Неизвестное имя';

    if (isTeacher) {
      if (!this.mediaStore.teacherMedia) {
        this.mediaStore.setTeacherMedia(peerId, displayName, userId);
        return;
      }

      this.mediaStore.updateTeacherMediaName(displayName, userId);
      return;
    }

    if (isIndividualLesson) {
      this.mediaStore.selectMedia(peerId);
    }

    if (!this.mediaStore.remoteMedia.has(peerId)) {
      this.mediaStore.setRemoteMedia(peerId, displayName, userId);
      return;
    }

    this.mediaStore.updateRemoteMediaName(peerId, displayName, userId);
  }

  setCurrentScalabilityLayer(peerId, consumerId, layers, isTeacher) {
    console.log('Mediasoup setCurrentScalabilityLayer', { peerId, consumerId, layers, isTeacher });
    if (isTeacher) {
      this.mediaStore.setTeacherMediaCurrentScalabilityLayer(consumerId, layers);
    } else {
      this.mediaStore.setRemoteMediaCurrentScalabilityLayer(peerId, consumerId, layers);
    }
  }

  setPreferredScalabilityLayer(peerId, consumerId, layers, isTeacher) {
    console.log('Mediasoup setPreferredScalabilityLayer', {
      peerId,
      consumerId,
      layers,
      isTeacher,
    });
    if (isTeacher) {
      this.mediaStore.setTeacherMediaPreferredScalabilityLayer(consumerId, layers);
    } else {
      this.mediaStore.setRemoteMediaPreferredScalabilityLayer(peerId, consumerId, layers);
    }
  }

  async closeConsumer(roomId, peerId, producerId, mediaType, isTeacher) {
    let remoteMedia;

    if (isTeacher) {
      remoteMedia = this.mediaStore.teacherMedia;
    } else {
      remoteMedia = this.mediaStore.remoteMedia.get(peerId);
    }

    if (!remoteMedia) {
      return;
    }

    if (remoteMedia.blockedStreams.streamsBlocking[mediaType]) {
      if (isTeacher) {
        this.mediaStore.removeTeacherBlockedStreams(mediaType);
      } else {
        this.mediaStore.removeRemoteMediaBlockedStreams(peerId, mediaType);
      }

      return;
    }

    const currentProducerId = remoteMedia.media?.[mediaType]?.producerId;

    if (currentProducerId && currentProducerId !== producerId) {
      console.warn('Mediasoup closeConsumer skipped: stale stop event for previous producer', {
        roomId,
        peerId,
        stoppedProducerId: producerId,
        currentProducerId,
        mediaType,
        isTeacher,
      });

      return;
    }

    const producerData = remoteMedia.producers?.[producerId];

    if (!producerData) {
      console.warn('Mediasoup closeConsumer skipped: producer is already removed', {
        roomId,
        peerId,
        producerId,
        mediaType,
        isTeacher,
      });

      return;
    }

    if (producerData.type !== mediaType) {
      console.warn('Mediasoup closeConsumer skipped: producer mediaType mismatch', {
        roomId,
        peerId,
        producerId,
        expectedMediaType: mediaType,
        actualMediaType: producerData.type,
        isTeacher,
      });

      return;
    }

    const consumer = remoteMedia.media?.[mediaType]?.consumer;
    const { consumerId } = producerData;

    if (currentProducerId !== producerId) {
      console.warn('Mediasoup closeConsumer skipped: stale stop event for previous producer', {
        roomId,
        peerId,
        stoppedProducerId: producerId,
        currentProducerId,
        mediaType,
        isTeacher,
      });

      return;
    }

    try {
      if (consumerId) {
        await this.socketRequest(this.videoConferenceActions.stopConsumeStream, {
          roomId,
          consumerId,
        });
      }
    } catch (error) {
      console.error('Mediasoup closeConsumer stopConsumeStream failed', {
        roomId,
        peerId,
        producerId,
        mediaType,
        isTeacher,
        consumerId,
        error,
      });
    }

    try {
      if (consumer && !consumer.closed) {
        consumer.close();
      }
    } catch (error) {
      console.error('Mediasoup closeConsumer local close failed', {
        roomId,
        peerId,
        producerId,
        mediaType,
        isTeacher,
        consumerId,
        error,
      });
    }

    if (isTeacher) {
      this.mediaStore.removeTeacherMediaTrack(producerId, true);
    } else {
      this.mediaStore.removeRemoteMediaTrack(peerId, producerId, true);
    }
  }

  closeAllConsumers(isRemoveSelected = false) {
    console.log('MediaSoup closeAllConsumers', { isRemoveSelected });
    const { teacherMedia } = this.mediaStore ?? {};
    const { remoteMedia } = this.mediaStore ?? {};
    if (teacherMedia) {
      this.mediaStore.removeTeacherMediaTracks();
      this.mediaStore.resetTeacherBlockedStreams();
    }
    if (remoteMedia) {
      for (const peerId of remoteMedia.keys()) {
        this.mediaStore.removeRemoteMediaTracks(peerId);
        this.mediaStore.resetRemoteMediaBlockedStreams(peerId);
      }
    }
    if (isRemoveSelected) {
      this.mediaStore.unselectMedia();
    }
  }

  removeConsumer(peerId, isTeacher) {
    if (isTeacher) {
      this.mediaStore.removeTeacherMedia();
    } else {
      this.mediaStore.removeRemoteMedia(peerId);
    }
  }

  removeAllConsumers() {
    const mediaStore = this.mediaStore ?? {};
    const { teacherMedia } = mediaStore ?? {};
    const { remoteMedia } = mediaStore ?? {};
    if (teacherMedia) {
      const { peerId } = teacherMedia;
      this.removeConsumer(peerId, true);
    }
    if (remoteMedia) {
      for (const peerId of remoteMedia.keys()) {
        this.removeConsumer(peerId, false);
      }
    }
  }

  getAllRemoteMedias() {
    return this.mediaStore.getAllRemoteMedias();
  }

  getRemoteMedias() {
    return this.mediaStore.getRemoteMedias();
  }

  getRemoteMedia(peerId) {
    return this.mediaStore.remoteMedia.get(peerId);
  }

  async unconsume(roomId, peerId, consumerId, producerId, isTeacher) {
    return this.runConsumerTransportOperation('unconsume', async () => {
      console.log('Mediasoup unconsume', { roomId, consumerId });

      try {
        await this.socketRequest(this.videoConferenceActions.stopConsumeStream, {
          roomId,
          consumerId,
        });
      } catch (e) {
        console.error(e);
      }

      if (isTeacher) {
        this.mediaStore.removeTeacherMediaTrack(producerId);
      } else {
        this.mediaStore.removeRemoteMediaTrack(peerId, producerId);
      }
    });
  }

  async stopRemoteMediaLocally(roomId, peerId, mediaType, isTeacher) {
    console.log('Mediasoup stopRemoteMediaLocally', { peerId, mediaType, isTeacher });
    try {
      let remoteMedia;
      if (isTeacher) {
        remoteMedia = this.mediaStore.teacherMedia;
      } else {
        remoteMedia = this.mediaStore.remoteMedia.get(peerId);
      }
      if (remoteMedia && !remoteMedia.blockedStreams.currentBlockedStreams[mediaType]) {
        if (isTeacher) {
          this.mediaStore.createTeacherBlockedStreams(mediaType, true);
        } else {
          this.mediaStore.createRemoteMediaBlockedStreams(peerId, mediaType, true);
        }
        const currentBlockedStreams = isTeacher
          ? this.mediaStore.teacherMedia.blockedStreams.currentBlockedStreams[mediaType]
          : this.mediaStore.remoteMedia.get(peerId).blockedStreams.currentBlockedStreams[mediaType];

        const { consumerId, producerId } = currentBlockedStreams || {};

        if (consumerId && producerId) {
          await this.unconsume(roomId, peerId, consumerId, producerId, isTeacher);
        }
      }
    } catch (e) {
      console.error(e);
    }
  }

  async resumeRemoteMediaLocally(roomId, peerId, mediaType, isTeacher, scalabilityLevel) {
    console.log('Mediasoup resumeRemoteMediaLocally', {
      peerId,
      mediaType,
      isTeacher,
      scalabilityLevel,
    });
    try {
      let remoteMedia;
      if (isTeacher) {
        remoteMedia = this.mediaStore.teacherMedia;
      } else {
        remoteMedia = this.mediaStore.remoteMedia.get(peerId);
      }

      const setBlockingStreamsFlag = flag => {
        if (isTeacher) {
          this.mediaStore.setTeacherStreamsBlockingFlag(mediaType, flag);
        } else {
          this.mediaStore.setRemoteMediaStreamsBlockingFlag(peerId, mediaType, flag);
        }
      };

      const removeBlockedStreams = streamsBlockingFlag => {
        if (isTeacher) {
          this.mediaStore.removeTeacherBlockedStreams(mediaType, streamsBlockingFlag);
        } else {
          this.mediaStore.removeRemoteMediaBlockedStreams(peerId, mediaType, streamsBlockingFlag);
        }
      };

      if (remoteMedia) {
        if (remoteMedia.blockedStreams.currentBlockedStreams[mediaType]) {
          const { producerId } = remoteMedia.blockedStreams.currentBlockedStreams[mediaType];
          setBlockingStreamsFlag(false);
          if (producerId) {
            await this.consume(
              roomId,
              { producerId, mediaType },
              peerId,
              isTeacher,
              scalabilityLevel,
              true,
            );
          }
        }
        removeBlockedStreams(false);
      }
    } catch (e) {
      console.error(e);
    }
  }

  getForcedBlockedMediaStreams() {
    return this.mediaStore.forcedBlockedMediaStreams;
  }

  setForcedBlockedMediaStreams(mediaType) {
    this.mediaStore.setForcedBlockedMediaStreams(mediaType);
  }

  removeForcedBlockedMediaStreams(mediaType) {
    this.mediaStore.removeForcedBlockedMediaStreams(mediaType);
  }

  resetForcedBlockedMediaStreams() {
    this.mediaStore.resetForcedBlockedMediaStreams();
  }

  setNewMediaBlockedStreams(blockedStreamsData, isSetAsDefaultBlockedStreams) {
    this.mediaStore.setNewMediaBlockedStreams(blockedStreamsData);
    if (isSetAsDefaultBlockedStreams) {
      this.mediaStore.setDefaultNewMediaBlockedStreams(blockedStreamsData);
    }
  }

  _onProducerEvents = producer => {
    producer.on('trackended', () => {
      console.log('Mediasoup producer trackended EVENT');
    });
    producer.on('transportclose', () => {
      console.log('Mediasoup producer transportclose EVENT');
    });
  };

  _onConsumerEvents = consumer => {
    consumer.on('trackend', () => {
      console.log('Mediasoup consumer trackend EVENT');
    });

    consumer.on('transportclose', () => {
      console.log('Mediasoup consumer transportclose EVENT');
    });
  };

  async _loadDevice(rtpCapabilities) {
    const { Device } = mediasoupClient;
    const device = new Device();
    try {
      await device.load({ routerRtpCapabilities: rtpCapabilities });
    } catch (e) {
      console.error(e);
    }
    this.device = device;
  }

  _initTransports(roomId, transports) {
    // to do transport должен иметь поле id, а не transportId
    const [sendTransport, receiveTransport] = transports;

    const producerTransport = this.device.createSendTransport({
      ...sendTransport,
      id: sendTransport.transportId,
    });
    this._onTransportEvents(roomId, producerTransport);
    const consumerTransport = this.device.createRecvTransport({
      ...receiveTransport,
      id: receiveTransport.transportId,
    });
    this._onTransportEvents(roomId, consumerTransport);
    this.producerTransport = producerTransport;
    this.consumerTransport = consumerTransport;
  }

  _onTransportEvents(roomId, transport) {
    transport.on(this.transportEvents.connect, async (data, callback, errCallback) => {
      const { dtlsParameters } = data;
      this.socketRequest(this.transportActions.connectTransport, {
        roomId,
        dtlsParameters,
        transportId: transport.id,
      })
        .then(() => callback())
        .catch(e => errCallback(e));
    });

    transport.on(this.transportEvents.produce, async (data, callback, errCallback) => {
      const { kind, rtpParameters, appData } = data;
      const { mediaType } = appData;
      let action;
      switch (mediaType) {
        case mediaTypes.audioType:
          action = this.videoConferenceActions.startStreamByMicrophone;
          break;
        case mediaTypes.videoType:
          action = this.videoConferenceActions.startStreamByCamera;
          break;
        case mediaTypes.screenType:
          action = this.videoConferenceActions.startShareScreen;
          break;
        // no default
      }
      try {
        const response = await this.socketRequest(action, {
          roomId,
          kind,
          rtpParameters,
          producerTransportId: transport.id,
        });
        const { producerId } = response.data.payload.producer;
        callback({ id: producerId });
      } catch (e) {
        errCallback(e);
      }
    });

    transport.on(this.transportEvents.connectionstatechange, state => {
      console.log(
        `${
          transport.direction === 'send' ? 'producerTransport' : 'consumerTransport'
        } connectionstatechange EVENT`,
        state,
      );
    });
  }

  _defineStore() {
    this.mediaStore = store.getStore('mediaStore');
  }
}

export default Mediasoup;
